/* eslint-disable react/no-unescaped-entities */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/api';
import { services } from '../../services';
import { Button } from '../ui/button';

const PioneerUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [pioneerUsers, setPioneerUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingUser, setViewingUser] = useState<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<any>(null);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [userToRestore, setUserToRestore] = useState<any>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [itemsPerPage] = useState(10);

  const fetchPioneerUsers = async (page: number = 1) => {
    setLoading(true);
    setError(null);
    try {
      const response = await services.get(
        `${API_ENDPOINTS.PIONEER_USERS.LIST}?page=${page}&limit=${itemsPerPage}&includeDeleted=true`
      );
      
      console.log('🔍 Pioneer Users API Response:', response);
      console.log('🔍 Response data:', response.data);
      console.log('🔍 Response data type:', typeof response.data);
      console.log('🔍 Response data keys:', Object.keys(response.data || {}));

      // Handle different response structures
      let usersData: any[] = [];
      let paginationData: any = {};

       // Check if response.data exists and has data
       if (response.data) {
         // Case 1: Pioneer Users API structure (status: true)
         if (response.data.status !== undefined) {
           console.log('📊 Using Pioneer Users API structure (status-based)');
           if (response.data.status) {
             usersData = response.data.data || [];
             paginationData = response.data.pagination || {};
           } else {
             setError(response.data.message || 'Failed to fetch pioneer users');
             return;
           }
         }
         // Case 2: Standard structure with success field
         else if (response.data.success !== undefined) {
           console.log('📊 Using success-based structure');
           if (response.data.success) {
             usersData = response.data.data || [];
             paginationData = response.data.pagination || {};
           } else {
             setError(response.data.message || 'Failed to fetch pioneer users');
             return;
           }
         }
         // Case 3: Data is directly an array
         else if (Array.isArray(response.data)) {
           console.log('📊 Using array structure');
           usersData = response.data;
           paginationData = {};
         }
         // Case 4: Direct data structure (no success field)
         else if (response.data.data) {
           console.log('📊 Using direct data structure');
           usersData = response.data.data;
           paginationData = response.data.pagination || response.data.meta || {};
         }
         // Case 5: Check for other common structures
         else {
           console.log('📊 Trying alternative structure');
           usersData = response.data.users || response.data.items || response.data.results || [];
           paginationData = response.data.pagination || response.data.meta || response.data.pageInfo || {};
         }
       }

      console.log('📊 Extracted users data:', usersData);
      console.log('📊 Extracted pagination data:', paginationData);
      console.log('📊 Users data type:', typeof usersData);
      console.log('📊 Is users data array?', Array.isArray(usersData));

      // Ensure usersData is an array
      const usersArray = Array.isArray(usersData) ? usersData : [];
      console.log('📊 Users array length:', usersArray.length);
      
      // Filter valid users (with _id or id)
      const validUsers = usersArray.filter((user) => {
        const hasId = user && (user._id || user.id);
        if (!hasId) {
          console.log('⚠️ Invalid user (no ID):', user);
        }
        return hasId;
      });
      
      console.log('✅ Valid users count:', validUsers.length);
      console.log('✅ Valid users:', validUsers);
      
      setPioneerUsers(validUsers);
      setTotalPages(paginationData.totalPages || paginationData.pages || 1);
      setCurrentPage(paginationData.pageNo || paginationData.page || paginationData.currentPage || 1);
      setTotalUsers(paginationData.totalRecords || paginationData.total || paginationData.totalCount || validUsers.length);
      
      console.log('📊 State updated:', {
        pioneerUsers: validUsers.length,
        totalPages: paginationData.totalPages || paginationData.pages || 1,
        currentPage: paginationData.pageNo || paginationData.page || paginationData.currentPage || 1,
        totalUsers: paginationData.totalRecords || paginationData.total || paginationData.totalCount || validUsers.length
      });

    } catch (err: any) {
      console.error('❌ Failed to fetch pioneer users:', err);
      if (err.status === 401 || err.data?.message?.includes('Not authorized')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      setError(err.response?.data?.message || err.message || 'Failed to fetch pioneer users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPioneerUsers(1);
  }, []);

  // View user functions
  const handleViewUser = (user: any) => {
    setViewingUser(user);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewingUser(null);
  };

  // Delete user functions
  const handleDeleteUser = (user: any) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await services.delete(API_ENDPOINTS.PIONEER_USERS.DELETE(userToDelete._id || userToDelete.id));
      fetchPioneerUsers(currentPage);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    } catch (err: any) {
      console.error('Failed to delete pioneer user:', err);
      if (err.status === 401 || err.data?.message?.includes('Not authorized')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      setError(err.response?.data?.message || err.message || 'Failed to delete pioneer user');
    }
  };

  const cancelDeleteUser = () => {
    setShowDeleteConfirm(false);
    setUserToDelete(null);
  };

  // Restore user functions
  const handleRestoreUser = (user: any) => {
    setUserToRestore(user);
    setShowRestoreConfirm(true);
  };

  const confirmRestoreUser = async () => {
    if (!userToRestore) return;

    try {
      await services.patch(API_ENDPOINTS.PIONEER_USERS.RESTORE(userToRestore._id || userToRestore.id), {});
      fetchPioneerUsers(currentPage);
      setShowRestoreConfirm(false);
      setUserToRestore(null);
    } catch (err: any) {
      console.error('Failed to restore pioneer user:', err);
      if (err.status === 401 || err.data?.message?.includes('Not authorized')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      setError(err.response?.data?.message || err.message || 'Failed to restore pioneer user');
    }
  };

  const cancelRestoreUser = () => {
    setShowRestoreConfirm(false);
    setUserToRestore(null);
  };

  // Pagination handler
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchPioneerUsers(page);
  };

  return (
    <div>
      {/* Header */}
      <div className='mb-8'>
        <div>
          <h2
            className='text-3xl font-bold mb-2 font-dm-serif'
            style={{
              color: '#ede8c5',
              textShadow: '0 2px 6px rgba(0, 0, 0, 0.4), 0 0 10px rgba(187, 228, 37, 0.2)',
              letterSpacing: '0.5px',
            }}
          >
            Pioneer Users Management
          </h2>
          <p className='font-work-sans' style={{ color: '#ede8c5' }}>
            Manage and view all pioneer users
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className='mb-6 p-4 rounded-lg border' style={{ backgroundColor: '#0B222C', borderColor: '#EF4444' }}>
          <p className='text-sm font-work-sans' style={{ color: '#EF4444' }}>
            {error}
          </p>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className='flex items-center justify-center py-12'>
          <div className='text-center'>
            <div
              className='animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4'
              style={{ borderColor: '#095D52' }}
            ></div>
            <p className='text-lg font-work-sans' style={{ color: '#ede8c5' }}>
              Loading pioneer users...
            </p>
          </div>
        </div>
      ) : (
        <div
          className='rounded-lg overflow-hidden shadow-lg transition-all duration-300'
          style={{
            backgroundColor: '#0B222C',
            border: '2px solid #095D52',
            boxShadow:
              '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
            backgroundImage:
              'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
          }}
        >
          <div className='overflow-x-auto'>
            <table className='min-w-full'>
              <thead
                style={{
                  backgroundColor: '#095D52',
                  backgroundImage:
                    'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                }}
              >
                <tr>
                  <th
                    className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Zealy Username
                  </th>
                  <th
                    className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Email
                  </th>
                  <th
                    className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    XP Earned
                  </th>
                  <th
                    className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    VSTN Rewards
                  </th>
                  <th
                    className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Status
                  </th>
                  <th
                    className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody style={{ backgroundColor: '#0B222C' }} className='divide-y divide-gray-700'>
                {(() => {
                  console.log('🔍 Rendering table with pioneerUsers:', pioneerUsers);
                  console.log('🔍 pioneerUsers length:', pioneerUsers.length);
                  console.log('🔍 pioneerUsers type:', typeof pioneerUsers);
                  return null;
                })()}
                {pioneerUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className='px-6 py-12 text-center'>
                      <div className='text-center'>
                        <div
                          className='mx-auto h-16 w-16 mb-6 rounded-full flex items-center justify-center'
                          style={{
                            backgroundColor: 'rgba(9, 93, 82, 0.1)',
                            border: '2px solid #095D52',
                          }}
                        >
                          <svg
                            className='h-8 w-8'
                            style={{ color: '#b8c5c2' }}
                            fill='currentColor'
                            viewBox='0 0 20 20'
                          >
                            <path d='M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z' />
                          </svg>
                        </div>
                        <h3 className='text-xl font-semibold font-dm-serif mb-2' style={{ color: '#ede8c5' }}>
                          No Pioneer Users Found
                        </h3>
                        <p className='text-sm font-work-sans' style={{ color: '#b8c5c2' }}>
                          {loading ? 'Loading users...' : 'There are no pioneer users to display at the moment.'}
                        </p>
                        {!loading && (
                          <p className='text-xs font-work-sans mt-2' style={{ color: '#8a9ba8' }}>
                            Check your API connection or try refreshing the page.
                          </p>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  pioneerUsers.map((user: any) => (
                   <tr
                     key={user._id || user.id}
                     className='transition-colors'
                     style={{
                       opacity: user.isDeleted ? 0.7 : 1,
                       backgroundColor: user.isDeleted ? 'rgba(220, 38, 38, 0.05)' : 'transparent',
                     }}
                     onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = user.isDeleted ? 'rgba(220, 38, 38, 0.1)' : '#095D52')}
                     onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = user.isDeleted ? 'rgba(220, 38, 38, 0.05)' : 'transparent')}
                   >
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <div
                        className='text-sm font-medium font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        {String(user.zealyUsername || 'N/A')}
                      </div>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <div className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                        {String(user.email || 'N/A')}
                      </div>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <div className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                        {String(user.xpEarned || '0')}
                      </div>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <div className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                        {String(user.vstnRewards || '0')}
                      </div>
                    </td>
                     <td className='px-6 py-4 whitespace-nowrap'>
                       <span
                         className='inline-flex px-3 py-1 text-xs font-semibold rounded-full font-work-sans'
                         style={{
                           backgroundColor: user.isDeleted ? '#DC2626' : (user.isActive ? '#10B981' : '#EF4444'),
                           color: '#ede8c5',
                         }}
                       >
                         {user.isDeleted ? 'Deleted' : (user.isActive ? 'Active' : 'Inactive')}
                       </span>
                     </td>
                     <td className='px-6 py-4 whitespace-nowrap'>
                       <div className='flex space-x-2'>
                         <button
                           onClick={() => handleViewUser(user)}
                           className='p-2 rounded-lg transition-all duration-200 hover:scale-105 focus:outline-none'
                           style={{
                             backgroundColor: '#0B222C',
                             border: '1px solid #095D52',
                             color: '#ede8c5',
                             boxShadow:
                               '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                           }}
                           onMouseEnter={(e) => {
                             e.currentTarget.style.backgroundColor = '#095D52';
                             e.currentTarget.style.borderColor = '#bbe425';
                             e.currentTarget.style.boxShadow =
                               '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                           }}
                           onMouseLeave={(e) => {
                             e.currentTarget.style.backgroundColor = '#0B222C';
                             e.currentTarget.style.borderColor = '#095D52';
                             e.currentTarget.style.boxShadow =
                               '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)';
                           }}
                           title='View User Details'
                         >
                           <svg
                             className='w-4 h-4'
                             fill='none'
                             stroke='currentColor'
                             viewBox='0 0 24 24'
                           >
                             <path
                               strokeLinecap='round'
                               strokeLinejoin='round'
                               strokeWidth={2}
                               d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                             />
                             <path
                               strokeLinecap='round'
                               strokeLinejoin='round'
                               strokeWidth={2}
                               d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                             />
                           </svg>
                         </button>
                         {user.isDeleted ? (
                           <button
                             onClick={() => handleRestoreUser(user)}
                             className='p-2 rounded-lg transition-all duration-200 hover:scale-105 focus:outline-none'
                             style={{
                               backgroundColor: '#0B222C',
                               border: '1px solid #10B981',
                               color: '#ede8c5',
                               boxShadow:
                                 '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(16, 185, 129, 0.1)',
                             }}
                             onMouseEnter={(e) => {
                               e.currentTarget.style.backgroundColor = '#10B981';
                               e.currentTarget.style.borderColor = '#059669';
                               e.currentTarget.style.boxShadow =
                                 '0 4px 15px rgba(16, 185, 129, 0.4), 0 0 0 1px rgba(5, 150, 105, 0.3)';
                             }}
                             onMouseLeave={(e) => {
                               e.currentTarget.style.backgroundColor = '#0B222C';
                               e.currentTarget.style.borderColor = '#10B981';
                               e.currentTarget.style.boxShadow =
                                 '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(16, 185, 129, 0.1)';
                             }}
                             title='Restore User'
                           >
                             <svg
                               className='w-4 h-4'
                               fill='none'
                               stroke='currentColor'
                               viewBox='0 0 24 24'
                             >
                               <path
                                 strokeLinecap='round'
                                 strokeLinejoin='round'
                                 strokeWidth={2}
                                 d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                               />
                             </svg>
                           </button>
                         ) : (
                           <button
                             onClick={() => handleDeleteUser(user)}
                             className='p-2 rounded-lg transition-all duration-200 hover:scale-105 focus:outline-none'
                             style={{
                               backgroundColor: '#0B222C',
                               border: '1px solid #095D52',
                               color: '#ede8c5',
                               boxShadow:
                                 '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                             }}
                             onMouseEnter={(e) => {
                               e.currentTarget.style.backgroundColor = '#EF4444';
                               e.currentTarget.style.borderColor = '#DC2626';
                               e.currentTarget.style.boxShadow =
                                 '0 4px 15px rgba(239, 68, 68, 0.4), 0 0 0 1px rgba(220, 38, 38, 0.3)';
                             }}
                             onMouseLeave={(e) => {
                               e.currentTarget.style.backgroundColor = '#0B222C';
                               e.currentTarget.style.borderColor = '#095D52';
                               e.currentTarget.style.boxShadow =
                                 '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)';
                             }}
                             title='Delete User'
                           >
                             <svg
                               className='w-4 h-4'
                               fill='none'
                               stroke='currentColor'
                               viewBox='0 0 24 24'
                             >
                               <path
                                 strokeLinecap='round'
                                 strokeLinejoin='round'
                                 strokeWidth={2}
                                 d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                               />
                             </svg>
                           </button>
                         )}
                       </div>
                     </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className='mt-8' style={{ borderTop: '2px solid #095D52' }}>
          <div className='flex justify-between items-center pt-6'>
            <div className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
              Page {currentPage} of {totalPages} • Showing{' '}
              {(currentPage - 1) * itemsPerPage + 1} to{' '}
              {Math.min(currentPage * itemsPerPage, pioneerUsers.length)} of {totalUsers} users
            </div>

            <div className='flex space-x-3'>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className='flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-work-sans'
                style={{
                  backgroundColor: currentPage === 1 ? '#095D5250' : '#0B222C',
                  border: '1px solid #095D52',
                  color: '#ede8c5',
                  boxShadow:
                    currentPage === 1
                      ? 'none'
                      : '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== 1) {
                    e.currentTarget.style.backgroundColor = '#095D52';
                    e.currentTarget.style.borderColor = '#bbe425';
                    e.currentTarget.style.boxShadow =
                      '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== 1) {
                    e.currentTarget.style.backgroundColor = '#0B222C';
                    e.currentTarget.style.borderColor = '#095D52';
                    e.currentTarget.style.boxShadow =
                      '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)';
                  }
                }}
              >
                <svg className='w-4 h-4 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M15 19l-7-7 7-7'
                  />
                </svg>
                Previous
              </button>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className='flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-work-sans'
                style={{
                  backgroundColor: currentPage === totalPages ? '#095D5250' : '#0B222C',
                  border: '1px solid #095D52',
                  color: '#ede8c5',
                  boxShadow:
                    currentPage === totalPages
                      ? 'none'
                      : '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== totalPages) {
                    e.currentTarget.style.backgroundColor = '#095D52';
                    e.currentTarget.style.borderColor = '#bbe425';
                    e.currentTarget.style.boxShadow =
                      '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== totalPages) {
                    e.currentTarget.style.backgroundColor = '#0B222C';
                    e.currentTarget.style.borderColor = '#095D52';
                    e.currentTarget.style.boxShadow =
                      '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)';
                  }
                }}
              >
                Next
                <svg className='w-4 h-4 ml-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M9 5l7 7-7 7'
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View User Modal */}
      {showViewModal && viewingUser && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div
            className='rounded-xl border max-w-4xl w-full mx-4 shadow-lg max-h-[90vh] flex flex-col'
            style={{
              backgroundColor: '#0B222C',
              borderColor: '#095D52',
              boxShadow:
                '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
            }}
          >
            {/* Header */}
            <div className='p-6 border-b border-gray-700 flex-shrink-0'>
              <div className='flex justify-between items-center'>
                <h2
                  className='text-2xl font-bold font-dm-serif'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 15px rgba(187, 228, 37, 0.3)',
                    letterSpacing: '0.8px',
                  }}
                >
                  Pioneer User Details
                </h2>
                <button
                  onClick={closeViewModal}
                  className='text-2xl transition-all duration-300 hover:scale-110'
                  style={{ color: '#ede8c5' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#bbe425')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#ede8c5')}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className='flex-1 overflow-y-auto p-6'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                {/* Basic Information */}
                <div className='space-y-4'>
                  <h3 className='text-lg font-semibold font-dm-serif mb-4' style={{ color: '#ede8c5' }}>
                    Basic Information
                  </h3>
                  
                  <div>
                    <label className='block text-sm font-medium font-work-sans mb-2' style={{ color: '#b8c5c2' }}>
                      Zealy Username
                    </label>
                    <div
                      className='w-full px-4 py-3 rounded-lg font-work-sans text-sm'
                      style={{
                        backgroundColor: 'rgba(9, 93, 82, 0.1)',
                        border: '1px solid #095D52',
                        color: '#ede8c5',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                      }}
                    >
                      {String(viewingUser.zealyUsername || 'N/A')}
                    </div>
                  </div>

                  <div>
                    <label className='block text-sm font-medium font-work-sans mb-2' style={{ color: '#b8c5c2' }}>
                      Email
                    </label>
                    <div
                      className='w-full px-4 py-3 rounded-lg font-work-sans text-sm'
                      style={{
                        backgroundColor: 'rgba(9, 93, 82, 0.1)',
                        border: '1px solid #095D52',
                        color: '#ede8c5',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                      }}
                    >
                      {String(viewingUser.email || 'N/A')}
                    </div>
                  </div>

                  <div>
                    <label className='block text-sm font-medium font-work-sans mb-2' style={{ color: '#b8c5c2' }}>
                      Telegram Account
                    </label>
                    <div
                      className='w-full px-4 py-3 rounded-lg font-work-sans text-sm'
                      style={{
                        backgroundColor: 'rgba(9, 93, 82, 0.1)',
                        border: '1px solid #095D52',
                        color: '#ede8c5',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                      }}
                    >
                      {String(viewingUser.tgAccount || 'N/A')}
                    </div>
                  </div>

                  <div>
                    <label className='block text-sm font-medium font-work-sans mb-2' style={{ color: '#b8c5c2' }}>
                      Status
                    </label>
                     <div
                       className='w-full px-4 py-3 rounded-lg font-work-sans text-sm'
                       style={{
                         backgroundColor: 'rgba(9, 93, 82, 0.1)',
                         border: '1px solid #095D52',
                         color: '#ede8c5',
                         boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                       }}
                     >
                       <span
                         className='inline-flex px-3 py-1 text-xs font-semibold rounded-full'
                         style={{
                           backgroundColor: viewingUser.isDeleted ? '#DC2626' : (viewingUser.isActive ? '#10B981' : '#EF4444'),
                           color: '#ede8c5',
                         }}
                       >
                         {viewingUser.isDeleted ? 'Deleted' : (viewingUser.isActive ? 'Active' : 'Inactive')}
                       </span>
                     </div>
                  </div>
                </div>

                {/* Rewards & Performance */}
                <div className='space-y-4'>
                  <h3 className='text-lg font-semibold font-dm-serif mb-4' style={{ color: '#ede8c5' }}>
                    Rewards & Performance
                  </h3>
                  
                  <div>
                    <label className='block text-sm font-medium font-work-sans mb-2' style={{ color: '#b8c5c2' }}>
                      XP Earned
                    </label>
                    <div
                      className='w-full px-4 py-3 rounded-lg font-work-sans text-sm'
                      style={{
                        backgroundColor: 'rgba(9, 93, 82, 0.1)',
                        border: '1px solid #095D52',
                        color: '#ede8c5',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                      }}
                    >
                      {String(viewingUser.xpEarned || '0')}
                    </div>
                  </div>

                  <div>
                    <label className='block text-sm font-medium font-work-sans mb-2' style={{ color: '#b8c5c2' }}>
                      VSTN Rewards
                    </label>
                    <div
                      className='w-full px-4 py-3 rounded-lg font-work-sans text-sm'
                      style={{
                        backgroundColor: 'rgba(9, 93, 82, 0.1)',
                        border: '1px solid #095D52',
                        color: '#ede8c5',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                      }}
                    >
                      {String(viewingUser.vstnRewards || '0')}
                    </div>
                  </div>

                  <div>
                    <label className='block text-sm font-medium font-work-sans mb-2' style={{ color: '#b8c5c2' }}>
                      Pool Share
                    </label>
                    <div
                      className='w-full px-4 py-3 rounded-lg font-work-sans text-sm'
                      style={{
                        backgroundColor: 'rgba(9, 93, 82, 0.1)',
                        border: '1px solid #095D52',
                        color: '#ede8c5',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                      }}
                    >
                      {String(viewingUser.poolShare || '0')}
                    </div>
                  </div>

                  <div>
                    <label className='block text-sm font-medium font-work-sans mb-2' style={{ color: '#b8c5c2' }}>
                      Wallet Address
                    </label>
                    <div
                      className='w-full px-4 py-3 rounded-lg font-work-sans text-sm break-all'
                      style={{
                        backgroundColor: 'rgba(9, 93, 82, 0.1)',
                        border: '1px solid #095D52',
                        color: '#ede8c5',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                      }}
                    >
                      {String(viewingUser.walletAddress || 'N/A')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Timeline Information */}
              <div className='mt-8'>
                <h3 className='text-lg font-semibold font-dm-serif mb-4' style={{ color: '#ede8c5' }}>
                  Timeline
                </h3>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  {viewingUser.createdAt && (
                    <div>
                      <label className='block text-sm font-medium font-work-sans mb-2' style={{ color: '#b8c5c2' }}>
                        Created At
                      </label>
                      <div
                        className='w-full px-4 py-3 rounded-lg font-work-sans text-sm'
                        style={{
                          backgroundColor: 'rgba(9, 93, 82, 0.1)',
                          border: '1px solid #095D52',
                          color: '#ede8c5',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                        }}
                      >
                        {new Date(viewingUser.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                  )}

                  {viewingUser.updatedAt && (
                    <div>
                      <label className='block text-sm font-medium font-work-sans mb-2' style={{ color: '#b8c5c2' }}>
                        Last Updated
                      </label>
                      <div
                        className='w-full px-4 py-3 rounded-lg font-work-sans text-sm'
                        style={{
                          backgroundColor: 'rgba(9, 93, 82, 0.1)',
                          border: '1px solid #095D52',
                          color: '#ede8c5',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                        }}
                      >
                        {new Date(viewingUser.updatedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                  )}

                  {viewingUser.verificationDate && (
                    <div>
                      <label className='block text-sm font-medium font-work-sans mb-2' style={{ color: '#b8c5c2' }}>
                        Verification Date
                      </label>
                      <div
                        className='w-full px-4 py-3 rounded-lg font-work-sans text-sm'
                        style={{
                          backgroundColor: 'rgba(9, 93, 82, 0.1)',
                          border: '1px solid #095D52',
                          color: '#ede8c5',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                        }}
                      >
                        {new Date(viewingUser.verificationDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                  )}

                  {viewingUser.distributionDate && (
                    <div>
                      <label className='block text-sm font-medium font-work-sans mb-2' style={{ color: '#b8c5c2' }}>
                        Distribution Date
                      </label>
                      <div
                        className='w-full px-4 py-3 rounded-lg font-work-sans text-sm'
                        style={{
                          backgroundColor: 'rgba(9, 93, 82, 0.1)',
                          border: '1px solid #095D52',
                          color: '#ede8c5',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                        }}
                      >
                        {new Date(viewingUser.distributionDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className='p-6 border-t border-gray-700 flex-shrink-0'>
              <div className='flex justify-end'>
                <Button
                  type='button'
                  onClick={closeViewModal}
                  className='font-work-sans transition-all duration-300 hover:scale-105 px-8 py-3'
                  style={{
                    backgroundColor: '#095D52',
                    color: '#ede8c5',
                    border: '1px solid #095D52',
                    boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                    backgroundImage:
                      'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#bbe425';
                    e.currentTarget.style.boxShadow =
                      '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#095D52';
                    e.currentTarget.style.boxShadow =
                      '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

       {/* Delete User Confirmation Modal */}
       {showDeleteConfirm && userToDelete && (
         <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
           <div
             className='rounded-xl border max-w-md w-full mx-4 shadow-lg'
             style={{
               backgroundColor: '#0B222C',
               borderColor: '#095D52',
               boxShadow:
                 '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
               backgroundImage:
                 'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
             }}
           >
             <div className='p-6'>
               <div className='flex items-center mb-4'>
                 <div className='flex-shrink-0'>
                   <svg
                     className='w-8 h-8'
                     style={{ color: '#EF4444' }}
                     fill='currentColor'
                     viewBox='0 0 20 20'
                   >
                     <path
                       fillRule='evenodd'
                       d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                       clipRule='evenodd'
                     />
                   </svg>
                 </div>
                 <div className='ml-3'>
                   <h3 className='text-lg font-medium font-dm-serif' style={{ color: '#ede8c5' }}>
                     Delete Pioneer User
                   </h3>
                 </div>
               </div>

               <div className='mb-6'>
                 <p className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                   {userToDelete.isDeleted ? (
                     <>
                       This user is already deleted. Are you sure you want to permanently remove{' '}
                       <span className='font-semibold' style={{ color: '#ede8c5' }}>
                         "{String(userToDelete.zealyUsername || 'this user')}"
                       </span>
                       {' '}from the system?
                     </>
                   ) : (
                     <>
                       Are you sure you want to delete{' '}
                       <span className='font-semibold' style={{ color: '#ede8c5' }}>
                         "{String(userToDelete.zealyUsername || 'this user')}"
                       </span>
                       ?
                     </>
                   )}
                 </p>
               </div>

               <div className='flex space-x-3'>
                 <button
                   onClick={cancelDeleteUser}
                   className='flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 font-work-sans'
                   style={{
                     backgroundColor: '#095D52',
                     color: '#ede8c5',
                     border: '1px solid #095D52',
                   }}
                   onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#bbe425')}
                   onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#095D52')}
                 >
                   Cancel
                 </button>
                 <button
                   onClick={confirmDeleteUser}
                   className='flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 font-work-sans'
                   style={{ backgroundColor: '#EF4444', color: '#ede8c5' }}
                   onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#DC2626')}
                   onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#EF4444')}
                 >
                   {userToDelete.isDeleted ? 'Permanently Remove' : 'Delete User'}
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}

       {/* Restore User Confirmation Modal */}
       {showRestoreConfirm && userToRestore && (
         <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
           <div
             className='rounded-xl border max-w-md w-full mx-4 shadow-lg'
             style={{
               backgroundColor: '#0B222C',
               borderColor: '#10B981',
               boxShadow:
                 '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(16, 185, 129, 0.1)',
               backgroundImage:
                 'linear-gradient(135deg, rgba(16, 185, 129, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
             }}
           >
             <div className='p-6'>
               <div className='flex items-center mb-4'>
                 <div className='flex-shrink-0'>
                   <svg
                     className='w-8 h-8'
                     style={{ color: '#10B981' }}
                     fill='currentColor'
                     viewBox='0 0 20 20'
                   >
                     <path
                       fillRule='evenodd'
                       d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                       clipRule='evenodd'
                     />
                   </svg>
                 </div>
                 <div className='ml-3'>
                   <h3 className='text-lg font-medium font-dm-serif' style={{ color: '#ede8c5' }}>
                     Restore Pioneer User
                   </h3>
                 </div>
               </div>

               <div className='mb-6'>
                 <p className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                   Are you sure you want to restore{' '}
                   <span className='font-semibold' style={{ color: '#ede8c5' }}>
                     "{String(userToRestore.zealyUsername || 'this user')}"
                   </span>
                   {' '}back to active status?
                 </p>
                 <p className='text-xs font-work-sans mt-2' style={{ color: '#b8c5c2' }}>
                   This will make the user active again and they will appear in the main user list.
                 </p>
               </div>

               <div className='flex space-x-3'>
                 <button
                   onClick={cancelRestoreUser}
                   className='flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 font-work-sans'
                   style={{
                     backgroundColor: '#095D52',
                     color: '#ede8c5',
                     border: '1px solid #095D52',
                   }}
                   onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#bbe425')}
                   onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#095D52')}
                 >
                   Cancel
                 </button>
                 <button
                   onClick={confirmRestoreUser}
                   className='flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 font-work-sans'
                   style={{ backgroundColor: '#10B981', color: '#ede8c5' }}
                   onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#059669')}
                   onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#10B981')}
                 >
                   Restore User
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}
    </div>
  );
};

export default PioneerUsersPage;
